import { Gauge, AlertTriangle, CheckCircle2, Clock, User, Wrench } from 'lucide-react';
import { useOrgTimezone, formatDateInTz } from '../../utils/timezone';
import { toNum } from '@/utils/numeric';
import type { EquipmentCalibration } from '../../types/spray';

interface CalibrationStatusCardProps {
  calibration?: EquipmentCalibration | null;
  equipmentName?: string | null;
  /** Renders the "required before start" state. */
  required?: boolean;
  /** The requirement has been satisfied by a calibration done for this spray. */
  satisfied?: boolean;
  /**
   * This is the calibration the spray was RUN at, read back off the finished
   * record rather than off the machine. It is history: it cannot be acted on,
   * and its age says nothing about whether the sprayer needs recalibrating now.
   */
  asApplied?: boolean;
  onCalibrate?: () => void;
  /** Creator-facing: show the toggle for demanding a calibration. */
  onToggleRequired?: (next: boolean) => void;
  compact?: boolean;
}

function methodLabel(m: EquipmentCalibration['method']): string {
  return m === 'nozzle' ? 'single-jet measurement' : 'timed tank';
}

/**
 * What a sprayer was last measured at, when, and by whom — the thing a creator
 * reads before deciding whether this job needs a fresh calibration, and the
 * thing an operator is shown when a spray refuses to start without one.
 *
 * "Never calibrated" is rendered as a first-class state rather than an empty
 * card: it is the most important thing this component can say, and the one
 * most likely to be true the first time somebody opens it.
 */
function CalibrationStatusCard({
  calibration,
  equipmentName,
  required = false,
  satisfied = false,
  asApplied = false,
  onCalibrate,
  onToggleRequired,
  compact = false,
}: CalibrationStatusCardProps) {
  const timezone = useOrgTimezone();

  const outstanding = required && !satisfied && !asApplied;
  // Staleness is advice about the NEXT job. Printed over a finished record it
  // reads as a fault in the spray that has already gone out, which it is not.
  const stale = !asApplied && calibration?.is_stale === true;

  const tone = outstanding ? 'border-amber-300 bg-amber-50'
    : stale                ? 'border-amber-200 bg-amber-50/50'
    : calibration          ? 'border-neutral-200 bg-white'
    :                        'border-neutral-300 bg-neutral-50';

  return (
    <div className={`rounded-lg border p-4 space-y-3 ${tone}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Gauge className="h-5 w-5 text-primary-600 shrink-0" />
          <div className="min-w-0">
            <h3 className="font-medium text-sm text-neutral-800">
              {asApplied ? 'Calibration this spray was applied at' : 'Sprayer calibration'}
            </h3>
            {equipmentName && (
              <p className="text-xs text-neutral-500 truncate">{equipmentName}</p>
            )}
          </div>
        </div>

        {onCalibrate && (
          <button
            type="button"
            onClick={onCalibrate}
            className="flex items-center gap-1.5 bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors shrink-0"
          >
            <Wrench className="h-3.5 w-3.5" />
            {calibration ? 'Recalibrate' : 'Calibrate'}
          </button>
        )}
      </div>

      {/* The block */}
      {outstanding && (
        <div className="flex items-start gap-1.5">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <strong>Calibration required before this spray can start.</strong>{' '}
            Run the sprayer and record what it actually puts out — the rate on file has not
            been verified for this job.
          </p>
        </div>
      )}

      {required && satisfied && !asApplied && (
        <p className="text-sm text-green-700 flex items-start gap-1.5">
          <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
          Calibration completed for this spray.
        </p>
      )}

      {/* What the machine was last measured at */}
      {calibration ? (
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <span className="text-lg font-semibold text-neutral-900">
              {toNum(calibration.output_rate_lpha)} L/ha
            </span>
            <span className="text-sm text-neutral-600">
              {toNum(calibration.total_flow_lpm)} L/min at {toNum(calibration.speed_kph)} km/h
            </span>
          </div>

          {!compact && (
            <p className="text-xs text-neutral-500">
              Swath {toNum(calibration.reference_swath_m)} m
              {/* An airblast's stored rate is only a reference — say so, or a
                  reader will take it for the rate every block gets. */}
              {calibration.swath_source === 'row_spacing' &&
                ' (reference row spacing — each block is costed at its own)'}
              {' · '}
              {methodLabel(calibration.method)}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-500">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDateInTz(calibration.calibrated_at, timezone)}
              {/* Age is only useful as "is this due a recalibration". On a
                  finished record the date alone is the fact. */}
              {!asApplied && calibration.age_days != null && ` · ${calibration.age_days} days ago`}
            </span>
            {(calibration.calibrated_by_first_name || calibration.calibrated_by_last_name) && (
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" />
                {calibration.calibrated_by_first_name} {calibration.calibrated_by_last_name}
              </span>
            )}
          </div>

          {stale && (
            <p className="text-xs text-amber-700 flex items-start gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              Older than your organisation&rsquo;s {calibration.valid_days}-day limit — worth
              recalibrating before this job.
            </p>
          )}
        </div>
      ) : asApplied ? (
        <p className="text-sm text-neutral-600">
          <strong>No calibration on record for this spray.</strong> The water volumes and tank
          counts on it came from figures typed onto the machine, not from anything it was
          measured doing.
        </p>
      ) : (
        <p className="text-sm text-neutral-600">
          <strong>Never calibrated.</strong> Any water volume or tank count for this sprayer is
          an estimate from figures someone typed, not from anything the machine has been
          measured doing.
        </p>
      )}

      {/* Creator control */}
      {onToggleRequired && (
        <label className="flex items-start gap-2 pt-2 border-t border-neutral-200/70 cursor-pointer">
          <input
            type="checkbox"
            checked={required}
            onChange={e => onToggleRequired(e.target.checked)}
            className="mt-0.5"
          />
          <span className="text-sm text-neutral-700">
            Require a fresh calibration before this spray can start
            <span className="block text-xs text-neutral-500">
              The operator is taken to the calibration screen and cannot start until it is done.
            </span>
          </span>
        </label>
      )}
    </div>
  );
}

export default CalibrationStatusCard;
